import { useRef } from 'react';
import { useRouter } from 'next/router';
import { useUI } from 'contexts';
import { IMAGE_QUERY_FIELDS, LINK_QUERY_FIELDS } from 'lib/contentful/fragments';
import {
  createArticleGetterFromCollection,
  createMicroCopyGetterFromCollection,
  getLinkHref,
} from 'lib/utils/helpers';
import { Image as SiteImage, RichText } from 'components/shared';
import Popup from 'components/shared/popup';
import { getResource } from 'lib/resources';

export const getNewsletterPopupQuery = ({ locale = 'en' }) => `
{
  globalModuleCollection(
    locale: "${locale}"
    limit: 1
    where: { name_contains: "Newsletter popup" }
  ) {
    items {
      name
      sys {
        id
      }
      resourcesCollection {
        items {
          ... on ComponentMicrocopy {
            __typename
            name
            text
            tag
          }
          ... on ComponentLink {
            name
            ${LINK_QUERY_FIELDS}
          }
          ... on ComponentArticle {
            __typename
            name
            title
            titleImage {
              ${IMAGE_QUERY_FIELDS}
            }
            mobileTitleImage {
              ${IMAGE_QUERY_FIELDS}
            }
            article {
              json
            }
            image {
              ${IMAGE_QUERY_FIELDS}
            }
            mobileImage {
              ${IMAGE_QUERY_FIELDS}
            }
            imagePlacement
          }
        }
      }
    }
  }
}
`;

const getCloseButtonClassName = layout => {
  if (layout === 'imageCover') return 'text-white';
  if (layout === 'imageRight') return 'text-blue md:text-white';
  return 'text-white md:text-blue';
};

const NewsletterPopup = ({ newsletterPopup }) => {
  const { closeNewsletterPopup, displayNewsletterPopup, isDesktop } = useUI();
  const inputRef = useRef();
  const router = useRouter();

  const { resourcesCollection } = newsletterPopup?.globalModuleCollection?.items[0] || {};

  const getArticleByName = createArticleGetterFromCollection(resourcesCollection);

  const {
    imagePlacement: imagePlacementArray,
    titleImage,
    mobileTitleImage,
    image,
    mobileImage,
    title,
    article,
  } = getArticleByName('Newsletter popup article') || {};

  const layout = imagePlacementArray?.[0] || 'imageLeft'; // imagePlacementArray[0];

  const getMicrocopyByName = createMicroCopyGetterFromCollection(resourcesCollection);
  const popupButtonCopy = getMicrocopyByName('Newsletter popup button text');
  const popupEmailPlaceholderCopy = getMicrocopyByName('EnterYourEmail');

  const newsletterPageLink = getResource(resourcesCollection?.items || [])('Newsletter page')?.link;
  const newsletterPagePath = newsletterPageLink ? getLinkHref(newsletterPageLink) : '/newsletter';

  const handleOnSubmit = async e => {
    e.preventDefault();
    closeNewsletterPopup();
    router.push({
      pathname: newsletterPagePath,
      query: { email: inputRef.current.value },
    });
  };

  const imageValue = isDesktop ? image : mobileImage;
  const titleImageValue = isDesktop ? titleImage : mobileTitleImage;

  return (
    <Popup
      show={displayNewsletterPopup}
      onClose={closeNewsletterPopup}
      closeButtonClassName={getCloseButtonClassName(layout)}
    >
      <div
        className={`relative flex w-full flex-col md:h-[500px] md:max-w-[1000px] md:flex-row md:gap-[30px] ${
          layout === 'imageRight' ? 'flex-col-reverse md:flex-row-reverse' : ''
        } ${layout === 'imageCover' ? 'bg-black' : ''}`}
      >
        {imageValue && (
          <SiteImage
            className={
              layout === 'imageCover'
                ? 'absolute inset-0 hidden opacity-75 md:block'
                : 'relative hidden h-auto min-h-[150px] w-full md:block md:min-h-0 md:min-w-[40%]'
            }
            src={imageValue}
            priority
          />
        )}
        <div
          className={`flex flex-col gap-[20px] p-[35px] text-center md:justify-center md:text-left ${
            layout === 'imageCover'
              ? 'absolute inset-0 mx-auto max-w-[400px] items-center justify-center text-white md:text-center'
              : ''
          } ${layout === 'imageLeft' ? 'md:pr-[60px]' : ''} ${
            layout === 'imageRight' ? 'md:pl-[60px]' : ''
          }`}
        >
          {titleImageValue && (
            <SiteImage
              src={titleImageValue}
              quality={100}
              priority
              layout="responsive"
              className="mx-auto md:mx-0"
            />
          )}

          {title && (
            <h2 className="font-nimbus-sans-extd-d text-[20px] font-bold uppercase leading-[0.9] md:text-[35px]">
              {title}
            </h2>
          )}

          {article && (
            <div className="font-nimbus-sans-d text-[12px] leading-[1.3]">
              <RichText data={article} />
            </div>
          )}

          <form onSubmit={handleOnSubmit} className="flex w-full flex-col gap-[20px]">
            <input
              ref={inputRef}
              className="input w-full bg-white"
              type="email"
              placeholder={popupEmailPlaceholderCopy}
            />
            <button className="button w-full" type="submit">
              {popupButtonCopy}
            </button>
          </form>
        </div>
      </div>
    </Popup>
  );
};

export default NewsletterPopup;
